import { useMemo, useRef } from "react";
import { useTran } from "@/hooks";
import { makeUniqueValidator } from "@/features/settings/utils";
import { showToast } from "@/lib/utils/toast";
import { authStore } from "@/features/auth/stores";
import { settingsService } from "../services";

export const useSettingsProfileForm = () => {
  const { t } = useTran();
  const { user } = authStore;
  const userId = user?.userId || user?.id || user?._id || "";
  const validationCache = useRef({
    username: { value: null, result: undefined, timer: null },
    email: { value: null, result: undefined, timer: null },
  });

  const initialProfileValues = useMemo(
    () => ({
      username: user?.username || "",
      name: user?.name || "",
      email: user?.email || "",
      company: user?.companyName || user?.company || "",
    }),
    [user],
  );

  const uniqueUsernameValidator = useMemo(
    () => makeUniqueValidator(validationCache, userId, "username", t),
    [t, userId],
  );

  const uniqueEmailValidator = useMemo(
    () => makeUniqueValidator(validationCache, userId, "email", t),
    [t, userId],
  );

  const handleProfileSubmit = async (values, { setSubmitting }) => {
    try {
      const success = await settingsService.updateProfile({
        userId,
        username: values.username,
        name: values.name,
        email: values.email,
      });
      if (!success) throw new Error(t("settings.profileUpdateFailed"));
      showToast.success(t("settings.profileUpdated"));
      await authStore.refreshMe();
    } catch (error) {
      showToast.error(error?.message || t("settings.profileUpdateFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  return {
    initialProfileValues,
    handleProfileSubmit,
    uniqueUsernameValidator,
    uniqueEmailValidator,
  };
};
